import {
  IncomingInventorizationOrder,
  InitialInventorizationPallet,
  InventorizationStatusData,
} from '../services/configApi';

const LABEL_SEPARATOR = ' - ';
const MISSING_PART = '?';

/**
 * How an incoming order is named in the lookup: reference, customer, ETA and commodity,
 * because a reference alone does not tell two arrivals of the same customer apart.
 */
export const getIncomingOrderLabel = (order: IncomingInventorizationOrder): string => [
  order.reference,
  order.company_name,
  order.eta_date,
  order.commodity_name,
].map((part) => part || MISSING_PART).join(LABEL_SEPARATOR);

/**
 * An order named from what a record or a pallet carries on its own — the reference — leaving
 * the customer, ETA and commodity only the incoming orders know to getFullyNamedOrderOption.
 */
const getOrderOptionByOrderIdAndReference = (
  orderId: number | null | undefined,
  reference: string | null | undefined
): IncomingInventorizationOrder | null => (
  orderId == null ? null : {
    order_id: orderId,
    reference: reference ?? null,
    eta_date: null,
    company_name: null,
    commodity_name: null,
  }
);

/**
 * The order a record carries, as an option of the lookup. A record can point at an order older
 * than the window the lookup offers — an SSCC match takes the order off the pallet, whatever
 * its age — so it is kept rather than dropped, which would clear it the moment the operator
 * saved anything else on the record.
 */
export const getRecordOrderOption = (
  record: InventorizationStatusData
): IncomingInventorizationOrder | null => (
  getOrderOptionByOrderIdAndReference(record.order_id, record.order_ref)
);

/**
 * The same option, named in full once the incoming orders hold it: a record knows only its own
 * reference, while the lookup names an order by customer, ETA and commodity as well.
 */
export const getFullyNamedOrderOption = (
  order: IncomingInventorizationOrder | null,
  findIncomingOrderById: (orderId: number) => IncomingInventorizationOrder | undefined
): IncomingInventorizationOrder | null => (
  order && (findIncomingOrderById(order.order_id) ?? order)
);

/**
 * The order to fill in for a matched pallet: the one it arrived with, whatever its age. An
 * order older than the window the lookup offers is named by its reference alone.
 */
export const getMatchedPalletOrder = (
  pallet: InitialInventorizationPallet,
  findIncomingOrderById: (orderId: number) => IncomingInventorizationOrder | undefined
): IncomingInventorizationOrder | null => getFullyNamedOrderOption(
  getOrderOptionByOrderIdAndReference(pallet.order_id, pallet.order_ref),
  findIncomingOrderById
);
